import React from "react";
import Reveal from "./Reveal";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "../../lib/track";

/**
 * Structured Momentum Method - Compressed Strip.
 *
 * Compact presentation of the six method steps with one line of
 * plain-language microcopy per step, so the method reads as a real
 * sequence instead of abstract terminology. The full method lives
 * on /framework alongside the public five pillars.
 */
const STEPS = [
  { name: "Listen", micro: "Hear what keeps repeating." },
  { name: "Locate", micro: "Name where you actually are." },
  { name: "Clarify", micro: "Decide what matters most now." },
  { name: "Structure", micro: "Give it a working shape." },
  { name: "Activate", micro: "Take the next real step." },
  { name: "Prove", micro: "Let results confirm the path." },
];

const StructuredMomentumStrip = () => (
  <section
    id="structured-momentum-strip"
    data-testid="structured-momentum-strip-section"
    className="relative py-16 md:py-20 border-t border-gold/10"
  >
    <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
      <Reveal>
        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-5">
          The Method
        </p>
        <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-themed leading-tight tracking-tight font-light">
          Six steps from inner clarity to{" "}
          <span className="italic gold-gradient-text">outer movement.</span>
        </h2>
        <p
          data-testid="structured-momentum-strip-explainer"
          className="mt-5 max-w-xl mx-auto text-white/80 text-[15px] leading-relaxed"
        >
          The framework is the way we move from reflection into action.
        </p>
      </Reveal>

      <Reveal delay={120}>
        <div
          data-testid="structured-momentum-strip-chips"
          className="mt-9 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
        >
          {STEPS.map((s, i) => (
            <div
              key={s.name}
              data-testid={`smm-strip-step-${s.name.toLowerCase()}`}
              className="border border-gold/20 rounded-lg bg-white/[0.03] px-3 py-4 flex flex-col items-center gap-1.5"
            >
              <span className="text-[10px] tracking-[0.3em] text-gold/60 tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[12px] sm:text-[13px] tracking-[0.18em] uppercase text-gold/90">
                {s.name}
              </span>
              <span className="text-[12px] text-white/65 leading-snug">
                {s.micro}
              </span>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={220}>
        <a
          href="/framework#structured-momentum-method"
          data-testid="structured-momentum-strip-cta"
          onClick={() => trackEvent("smm_strip_full_method_clicked")}
          className="btn-ghost-gold mt-10 inline-flex items-center gap-2"
        >
          See the Full Method <ArrowRight size={13} />
        </a>
      </Reveal>
    </div>
  </section>
);

export default StructuredMomentumStrip;
